import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { surfaceClass, useDismissable } from './ui'
import { cn } from '@/lib/cn'
import { useDebouncedValue } from '@/lib/hooks'
import { useIssueSearch, useProjects } from '@/lib/queries'
import { useWorkspace } from '@/lib/workspace'
import type { MembershipSummary } from '@/lib/types'

interface Result {
  key: string
  kind: 'project' | 'issue'
  label: string
  detail: string
  to: string
}

/**
 * Workspace search.
 *
 * Projects are matched locally against the list the sidebar has already loaded,
 * so they appear on the first keystroke; issues go to `GET /api/tasks?search=`,
 * which is scoped server-side and escapes the term before it reaches SQL. It is
 * a combobox rather than a fancy palette: type, arrow, enter.
 */
export function SearchMenu({ membership }: { membership: MembershipSummary }) {
  const workspace = useWorkspace()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listId = useId()

  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [cursor, setCursor] = useState(0)
  const close = useCallback(() => setOpen(false), [])
  const ref = useDismissable(open, close)

  const debounced = useDebouncedValue(term, 250)
  const projects = useProjects(workspace.slug, membership.id)
  const issues = useIssueSearch(workspace.slug, debounced)

  // ⌘K / Ctrl+K focuses search from anywhere in the shell.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const results = useMemo<Result[]>(() => {
    const needle = debounced.trim().toLowerCase()
    if (needle.length === 0) return []

    const projectHits: Result[] = (projects.data ?? [])
      .filter((project) => project.name.toLowerCase().includes(needle))
      .slice(0, 4)
      .map((project) => ({
        key: `project:${project.id}`,
        kind: 'project',
        label: project.name,
        detail: `${project.tasks_count ?? 0} issues`,
        to: workspace.path(`/projects/${project.id}`),
      }))

    const issueHits: Result[] = (issues.data?.data ?? []).slice(0, 6).map((task) => ({
      key: `issue:${task.id}`,
      kind: 'issue',
      label: task.title,
      detail: task.project?.name ?? task.issue_key ?? 'Issue',
      to: `${workspace.path(`/projects/${task.project_id}`)}?task=${task.id}`,
    }))

    return [...projectHits, ...issueHits]
  }, [debounced, projects.data, issues.data, workspace])

  useEffect(() => {
    setCursor(0)
  }, [debounced])

  function choose(result: Result | undefined) {
    if (!result) return
    setOpen(false)
    setTerm('')
    inputRef.current?.blur()
    navigate(result.to)
  }

  function onKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      if (term) {
        setTerm('')
        setOpen(false)
        return
      }
      setOpen(false)
      inputRef.current?.blur()
      return
    }
    if (results.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setCursor((index) => (index + 1) % results.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setCursor((index) => (index - 1 + results.length) % results.length)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      choose(results[cursor])
    }
  }

  const expanded = open && debounced.trim().length > 0
  const activeId = expanded && results[cursor] ? `${listId}-${cursor}` : undefined

  return (
    <div className="relative w-full max-w-md" ref={ref}>
      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-400">
        <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true">
          <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="m13.5 13.5 3 3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
      <input
        ref={inputRef}
        // Not type="search": Chrome's native clear button lands on top of the
        // ⌘K hint. Escape already clears the field here.
        type="text"
        role="combobox"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={activeId}
        aria-autocomplete="list"
        aria-label="Search projects and issues"
        placeholder="Search projects and issues…"
        value={term}
        onChange={(event) => {
          setTerm(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className={cn(
          'h-9 w-full rounded-lg border border-ink-200 bg-white pr-14 pl-9 text-sm transition',
          'placeholder:text-ink-400 hover:border-ink-300 focus:border-brand-500',
          'dark:border-ink-800 dark:bg-ink-900 dark:hover:border-ink-700',
        )}
      />
      <kbd
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 rounded border border-ink-200 px-1.5 py-0.5 font-sans text-[10px] text-ink-400 dark:border-ink-700"
      >
        ⌘K
      </kbd>

      {expanded ? (
        <div
          id={listId}
          role="listbox"
          aria-label="Search results"
          className={cn(
            surfaceClass,
            'absolute top-full left-0 z-30 mt-1.5 w-full overflow-hidden p-1.5 shadow-lg shadow-ink-950/10',
          )}
        >
          {results.length === 0 ? (
            <p className="px-2.5 py-3 text-sm text-ink-500 dark:text-ink-400">
              {issues.isFetching ? 'Searching…' : `Nothing matches “${debounced.trim()}”.`}
            </p>
          ) : (
            results.map((result, index) => (
              <button
                key={result.key}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === cursor}
                type="button"
                onMouseEnter={() => setCursor(index)}
                onClick={() => choose(result)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition',
                  index === cursor ? 'bg-ink-100 dark:bg-ink-800' : '',
                )}
              >
                <span
                  className={cn(
                    'inline-flex size-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold',
                    result.kind === 'project'
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                      : 'bg-ink-100 text-ink-500 dark:bg-ink-800 dark:text-ink-300',
                  )}
                  aria-hidden="true"
                >
                  {result.kind === 'project' ? 'P' : '#'}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm">{result.label}</span>
                <span className="shrink-0 text-xs text-ink-500 dark:text-ink-400">{result.detail}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
